import {
    createStyles,
    Dialog,
    DialogActions,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    WithStyles,
    withStyles,
} from '@material-ui/core';
import { Form, Formik, FormikActions, FormikProps } from 'formik';
import _ from 'lodash';
import { inject } from 'mobx-react';
import * as React from 'react';
import FullWidthField from 'src/common/FullWidthField';
import { DefaultBranchKpi, IBranch, IBranchKpi, IKpi } from 'src/Definitions';
import { IdentifierEntityDto, SwaggerResponse } from 'src/generated_client';
import { IStores } from 'src/Stores';
import * as Yup from 'yup';
import Button from '../common/Button';

const styles = () =>
    createStyles({
        root: {
            marginTop: '10px',
            overflowX: 'auto',
            width: '100%',
        },
        table: {
            minWidth: 650,
        },
    });

const BranchKpiSchema = Yup.object().shape({
    branchKpi: Yup.object().shape({}),
});

interface IBranchKpiDialogProps extends WithStyles<typeof styles> {
    branches?: IBranch[];
    kpis?: IKpi[];
    branchKpis?: IBranchKpi[];
    close?: () => void;
    isLoading?: boolean;
    isOpen?: boolean;
    saveBranchKpi?: (
        branchId: string,
        branchKpi: IBranchKpi
    ) => Promise<void | SwaggerResponse<void>>;
    getAllBranchKpis?: () => void;
}

class BranchKpiDialog extends React.Component<IBranchKpiDialogProps> {
    public save = (values: { [key: string]: any }, formikBag: FormikActions<any>) => {
        const promises: Array<Promise<void>> = [];
        this.props.branches!.forEach((branch) => {
            this.props.kpis!.forEach((kpi) => {
                if (
                    values[this.getKeyForBranchKpi(branch, kpi)] !== undefined &&
                    values[this.getKeyForBranchKpi(branch, kpi)] !== null
                ) {
                    const branchKpiToSave = {
                        ...DefaultBranchKpi(true),
                        ...this.getBranchKpi(branch, kpi),
                        branch: this.getIdentifierEntityFromBranch(branch),
                        kpi: this.getIdentifierEntityFromKpi(kpi),
                        value: values[this.getKeyForBranchKpi(branch, kpi)],
                    } as IBranchKpi;
                    promises.push(
                        new Promise(() => {
                            this.props.saveBranchKpi!(branch.id, branchKpiToSave);
                        })
                    );
                }
            });
        });
        Promise.all(promises).finally(() => {
            formikBag.setSubmitting(false);
        });
        this.props.close!();
    };

    private getIdentifierEntityFromBranch(branch: IBranch) {
        return new IdentifierEntityDto({ name: branch.name, id: branch.id });
    }

    private getIdentifierEntityFromKpi(kpi: IKpi) {
        return new IdentifierEntityDto({ id: kpi.id, name: kpi.name });
    }

    private getBranchKpi(branch: IBranch, kpi: IKpi): IBranchKpi | undefined {
        if (this.props.branchKpis) {
            return this.props.branchKpis!.find((branchKpi) => {
                return branchKpi.branch.id === branch.id && branchKpi.kpi.id === kpi.id;
            });
        }
        return undefined;
    }

    private mapBranchKpisToForm() {
        const formikFields = {};
        if (this.props.branches && this.props.kpis) {
            this.props.branches.forEach((branch) => {
                this.props.kpis!.forEach((kpi) => {
                    formikFields[this.getKeyForBranchKpi(branch, kpi)] = this.getBranchKpiValue(
                        branch,
                        kpi
                    );
                });
            });
        }
        return formikFields;
    }

    private getKeyForBranchKpi(branch: IBranch, kpi: IKpi) {
        return `${branch.id}-${kpi.id}`;
    }

    private getBranchKpiValue(branch: IBranch, kpi: IKpi) {
        const branchKpi = this.getBranchKpi(branch, kpi);
        if (branchKpi) {
            return branchKpi.value;
        }
        return undefined;
    }

    private validateBranchKpiValue = (
        kpiTypeName: string | undefined,
        kpiMin: number,
        kpiMax: number
    ) => (value: number) => {
        if (kpiTypeName === 'Integer' && !Number.isInteger(value) && value) {
            return 'Please enter an integer value';
        }
        if (value < kpiMin) {
            return `Please enter a number greater than or equal to ${kpiMin}`;
        }
        if (value > kpiMax) {
            return `Please enter a number less than or equal to ${kpiMax}`;
        }
        return;
    };

    public render() {
        const { close, isLoading, isOpen = false } = this.props;
        return (
            <Dialog maxWidth="lg" onClose={close} open={isOpen}>
                <Formik
                    initialValues={this.mapBranchKpisToForm()}
                    onSubmit={this.save}
                    validationSchema={BranchKpiSchema}
                    enableReinitialize
                >
                    {(formikProps: FormikProps<any>) => (
                        <Form>
                            <DialogContent>
                                <Table className={this.props.classes.table}>
                                    {this.renderTableHeaders()}
                                    {this.renderTableBody()}
                                </Table>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    disabled={!formikProps.isValid}
                                    isLoading={
                                        isLoading ||
                                        formikProps.isValidating ||
                                        formikProps.isSubmitting
                                    }
                                    color="primary"
                                    type="submit"
                                    variant="contained"
                                >
                                    Save
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        );
    }

    private renderTableHeaders() {
        return (
            <TableHead>
                <TableRow key="branches">
                    <TableCell>
                        <Typography variant="h6" component="h2">
                            Branch Comparison
                        </Typography>
                    </TableCell>
                    {this.props.branches &&
                        this.props.branches.map((branch, idx) => (
                            <TableCell
                                key={branch.id}
                                align="center"
                                style={{
                                    backgroundColor: idx === 0 ? 'lightgrey' : 'white',
                                }}
                            >
                                <div>{idx === 0 ? 'Target Branch' : 'Comparison Branch'}</div>
                                <Typography
                                    key={`${branch.id} label`}
                                    variant="body2"
                                    component="h2"
                                >
                                    {branch.name}
                                </Typography>
                            </TableCell>
                        ))}
                </TableRow>
            </TableHead>
        );
    }

    private renderTableBody() {
        return (
            <TableBody>
                {this.props.kpis &&
                    this.props.kpis.map((kpi) => (
                        <TableRow key={kpi.name}>
                            <TableCell key={`${kpi.name} label`} component="th" scope="row">
                                {kpi.name}
                            </TableCell>
                            {this.props.branches &&
                                this.props.branches.map((branch, idx) => (
                                    <TableCell
                                        key={`${branch.name} ${kpi.name}`}
                                        align="right"
                                        style={{
                                            backgroundColor: idx === 0 ? 'lightgrey' : 'white',
                                        }}
                                    >
                                        <FullWidthField
                                            required={false}
                                            key={`${branch.name} ${kpi.name} label`}
                                            name={this.getKeyForBranchKpi(branch, kpi)}
                                            type="number"
                                            placeholder={kpi.kpiTypeName}
                                            validate={this.validateBranchKpiValue(
                                                kpi.kpiTypeName,
                                                kpi.lowerLimit,
                                                kpi.upperLimit
                                            )}
                                        />
                                    </TableCell>
                                ))}
                        </TableRow>
                    ))}
            </TableBody>
        );
    }
}

const InjectedBranchKpiDialog = inject<
    IStores,
    IBranchKpiDialogProps,
    Partial<IBranchKpiDialogProps>,
    any
>((stores: IStores) => ({
    branchKpis: stores.branchKpis.branchKpis,
    branches: stores.branchKpis.branches,
    close: stores.branchKpis.closeDialog,
    getAllBranchKpis: stores.branchKpis.getAllBranchKpis,
    isLoading: stores.branchKpis.isLoading,
    isOpen: stores.branchKpis.isModalOpen,
    kpis: stores.branchKpis.kpis,
    saveBranchKpi: stores.branchKpis.saveBranchKpi,
}))(withStyles(styles)(BranchKpiDialog));

export default InjectedBranchKpiDialog;



// WEBPACK FOOTER //
// ./src/branchKpis/BranchKpiDialog.tsx